import React, { useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  DialogContent,
  DialogActions,
  Container,
  Paper,
  IconButton,
  duration,
} from "@mui/material";
import Select from "react-select";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { Add, Remove } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useGetLocationsQuery } from "slices/common/locationSlice";
import {
  useGetSingleSubscriptionQuery,
  useAddSubscriptionMutation,
  useUpdateSubscriptionMutation,
} from "slices/beautyApp/subscriptionSlice";

function AddPlan() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const { data: locationsData } = useGetLocationsQuery({});
  const { data: initialData } = useGetSingleSubscriptionQuery(id, {
    skip: !isEdit,
  });

  const [add] = useAddSubscriptionMutation();
  const [update] = useUpdateSubscriptionMutation();

  /* ---------- Location Options ---------- */
  const locationOptions = useMemo(
    () =>
      locationsData?.data?.data?.map((loc) => ({
        value: loc._id,
        label: loc.cityName,
      })) || [],
    [locationsData]
  );

  console.log("Initial Data:", initialData);

  /* ---------- Initial Values ---------- */
  const initialValues = {
    name: initialData?.data?.name || "",
    description: initialData?.data?.description || "",
    features: initialData?.data?.features || [""],
    topUpPeriod: initialData?.data?.topUpPeriod || "",
    cityPricing:
      initialData?.data?.cityPricing?.map((l) => ({
        city: l.city._id, // ✅ IMPORTANT
        price: l.price,
        useLimit: l.useLimit,
        durationInDays: l.durationInDays,
      })) || [],
  };

  /* ---------- Validation ---------- */
  const validationSchema = Yup.object({
    name: Yup.string().required("Plan name is required"),
    description: Yup.string().required("Description is required"),

    topUpPeriod: Yup.number()
      .typeError("Top-up period must be a number")
      .moreThan(0, "Top-up period must be greater than 0")
      .required("Top-up period is required"),

    features: Yup.array()
      .of(Yup.string().required("Feature is required"))
      .min(1, "At least one feature is required"),

    cityPricing: Yup.array()
      .min(1, "At least one location is required")
      .of(
        Yup.object({
          city: Yup.string().required("Location is required"),
          price: Yup.number()
            .typeError("Price must be a number")
            .moreThan(0, "Price must be greater than 0")
            .required("Price is required"),
          useLimit: Yup.number()
            .typeError("Use limit must be a number")
            .moreThan(0, "Use limit must be greater than 0")
            .required("Use limit is required"),
        })
      ),
  });

  /* ---------- Submit ---------- */
  const handleSubmit = async (values) => {
    const payload = {
      name: values.name,
      description: values.description,
      features: values.features.filter(Boolean),
      topUpPeriod: Number(values.topUpPeriod),
      cityPricing: values.cityPricing.map((l) => ({
        city: l.city,
        price: Number(l.price),
        useLimit: Number(l.useLimit),
        durationInDays: 30, // constant
      })),
    };

    if (isEdit) {
      await update({ id, body: payload }).unwrap();
      navigate("/beauty-app/beautician-subscriptions", {
        state: { refresh: true },
      });
    } else {
      await add(payload).unwrap();
      navigate("/beauty-app/beautician-subscriptions", {
        state: { refresh: true },
      });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          {isEdit ? "Edit Plan" : "Add Plan"}
        </Typography>

        <Paper sx={{ p: 3 }}>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              handleSubmit,
            }) => {
              const selectedLocations = locationOptions.filter((opt) =>
                values.cityPricing.some((l) => l.city === opt.value)
              );

              return (
                <form onSubmit={handleSubmit}>
                  <DialogContent dividers>
                    {/* Name */}
                    <TextField
                      fullWidth
                      label="Plan Name"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Top-up Period (in months)"
                      name="topUpPeriod"
                      value={values.topUpPeriod}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.topUpPeriod && Boolean(errors.topUpPeriod)}
                      helperText={touched.topUpPeriod && errors.topUpPeriod}
                      sx={{ mb: 2 }}
                    />

                    {/* Description */}
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      sx={{ mb: 2 }}
                    />

                    {/* Features */}
                    <FieldArray name="features">
                      {(arrayHelpers) => (
                        <Box sx={{ mb: 3 }}>
                          <Typography fontWeight={600}>Features</Typography>

                          {values.features.map((feature, index) => (
                            <Box key={index} sx={{ display: "flex", mt: 1 }}>
                              <TextField
                                fullWidth
                                value={feature}
                                onChange={(e) =>
                                  setFieldValue(
                                    `features[${index}]`,
                                    e.target.value
                                  )
                                }
                                error={
                                  touched.features?.[index] &&
                                  Boolean(errors.features?.[index])
                                }
                                helperText={
                                  touched.features?.[index] &&
                                  errors.features?.[index]
                                }
                              />

                              {index > 0 && (
                                <IconButton
                                  color="error"
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  <Remove />
                                </IconButton>
                              )}

                              <IconButton
                                color="primary"
                                onClick={() =>
                                  arrayHelpers.insert(index + 1, "")
                                }
                              >
                                <Add />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </FieldArray>

                    {/* Locations */}
                    <Typography fontWeight={600}>
                      Location-wise Pricing
                    </Typography>

                    <Select
                      isMulti
                      options={locationOptions}
                      value={selectedLocations}
                      placeholder="Select locations"
                      onChange={(selected) => {
                        const mapped = selected.map((item) => {
                          const existing = values.cityPricing.find(
                            (l) => l.city === item.value
                          );

                          return (
                            existing || {
                              city: item.value,
                              price: "",
                              useLimit: "",
                            }
                          );
                        });

                        setFieldValue("cityPricing", mapped);
                      }}
                    />

                    {/* City Pricing Fields */}
                    {values.cityPricing.map((loc, index) => (
                      <Box
                        key={loc.city}
                        sx={{
                          mt: 2,
                          p: 2,
                          border: "1px solid #ddd",
                          borderRadius: 2,
                        }}
                      >
                        <Typography fontWeight={600}>
                          {
                            locationOptions.find((o) => o.value === loc.city)
                              ?.label
                          }
                        </Typography>

                        <TextField
                          label="Price"
                          type="number"
                          fullWidth
                          sx={{ mt: 1 }}
                          value={loc.price}
                          onChange={(e) =>
                            setFieldValue(
                              `cityPricing[${index}].price`,
                              e.target.value
                            )
                          }
                          error={
                            touched.cityPricing?.[index]?.price &&
                            Boolean(errors.cityPricing?.[index]?.price)
                          }
                          helperText={
                            touched.cityPricing?.[index]?.price &&
                            errors.cityPricing?.[index]?.price
                          }
                        />

                        <TextField
                          label="Use Limit"
                          type="number"
                          fullWidth
                          sx={{ mt: 1 }}
                          value={loc.useLimit}
                          onChange={(e) =>
                            setFieldValue(
                              `cityPricing[${index}].useLimit`,
                              e.target.value
                            )
                          }
                          error={
                            touched.cityPricing?.[index]?.useLimit &&
                            Boolean(errors.cityPricing?.[index]?.useLimit)
                          }
                          helperText={
                            touched.cityPricing?.[index]?.useLimit &&
                            errors.cityPricing?.[index]?.useLimit
                          }
                        />
                      </Box>
                    ))}
                  </DialogContent>

                  <DialogActions>
                    <Button type="submit" variant="contained">
                      {isEdit ? "Update Plan" : "Create Plan"}
                    </Button>
                  </DialogActions>
                </form>
              );
            }}
          </Formik>
        </Paper>
      </Container>
    </DashboardLayout>
  );
}

export default AddPlan;
